import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { PulseLogo } from "@/components/site/Header";
import PageTransition from "@/components/site/PageTransition";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If that email exists, a reset link was sent.");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center px-6 pt-24" data-testid="forgot-password-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md p-10 rounded-3xl border border-white/10"
          style={{ background: "linear-gradient(180deg, #121215, #0c0c0e)" }}
        >
          <div className="flex flex-col items-center mb-8">
            <PulseLogo size={56} />
            <div className="font-display text-2xl font-bold tracking-tight mt-3">Reset password</div>
            <div className="label-mono mt-1">/ Forgot</div>
          </div>
          {sent ? (
            <div className="text-center text-zinc-300 text-sm">
              <p>If <b>{email}</b> is registered, a reset link is on its way. The link is valid for 1 hour.</p>
              <Link to="/login" className="link-blue inline-block mt-6">← Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div>
                <label className="label-mono block mb-2">Email</label>
                <input className="pulse-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email"/>
              </div>
              <button disabled={busy} className="glass-btn solid w-full justify-center" data-testid="forgot-submit">
                {busy ? "Sending..." : "Send reset link"}
              </button>
              <div className="text-center text-xs text-zinc-500 pt-2">
                <Link to="/login" className="link-blue">← Back to sign in</Link>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </PageTransition>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/login");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Reset failed"); }
    finally { setBusy(false); }
  };

  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center px-6 pt-24" data-testid="reset-password-page">
        <div className="w-full max-w-md p-10 rounded-3xl border border-white/10" style={{ background: "linear-gradient(180deg, #121215, #0c0c0e)" }}>
          <div className="flex flex-col items-center mb-8">
            <PulseLogo size={56} />
            <div className="font-display text-2xl font-bold tracking-tight mt-3">Set new password</div>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <input className="pulse-input" placeholder="New password (min 6)" type="password" minLength={6} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw1"/>
            <input className="pulse-input" placeholder="Confirm new password" type="password" minLength={6} required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-pw2"/>
            <button disabled={busy || !token} className="glass-btn solid w-full justify-center" data-testid="reset-submit">
              {busy ? "Updating..." : "Update Password"}
            </button>
            {!token && <div className="text-rose-400 text-xs text-center">Missing token — open the link from your email.</div>}
          </form>
        </div>
      </div>
    </PageTransition>
  );
}
